import { useMemo } from "react";
import { CronData, fromCron } from "../cron/utils";
import { CalendarItemData, minutes } from "../calendar/utils/types";
import { calculateItemDuration } from "../calendar/utils";
import { timeOnDate } from "../calendar/utils/dates";
import { Availability } from "./types";

export type CronType = "daily" | "weekly" | "monthly";
export type AvailabilityType = "Event::Availability" | "Event::Unavailability";
export const AVAILABILITY_TYPES = {
  available: "Event::Availability" as AvailabilityType,
  unavailable: "Event::Unavailability" as AvailabilityType,
};


export type AvailabilityTimeslotData = {
  id: string;
  _destroy?: boolean;
  type: string;
  timeslot: {
    schedule: {
      cron: string;
      duration: string;
    };
  };
  cronData: CronData;
}

export function newCronStr(
  start: Date,
  type: CronType,
  timezone: string,
) {
  switch (type) {
    case "daily":
      return `${start.getMinutes()} ${start.getHours()} * * 1,2,3,4,5 ${timezone}`
    case "weekly":
      return `${start.getMinutes()} ${start.getHours()} * * ${start.getDay()} ${timezone}`
    case "monthly":
      return `0 0 ${start.getDate()} * * ${timezone}`
  }
}

const timeslotId = (type: CronType, start: Date, duration: number): string =>
  `new_${type}_${start.getTime()}_${duration}`;

export function newAvailabilityTimeslot(
  start: Date,
  end: Date,
  slotSize: minutes,
  type: CronType,
  available: AvailabilityType,
  timezone: string,
): AvailabilityTimeslotData {
  const duration = calculateItemDuration(start, end, slotSize, type === "monthly" ? null : (1440 - slotSize));
  const cronString = newCronStr(start, type, timezone);
  return {
    id: timeslotId(type, start, duration),
    type: available,
    timeslot: {
      schedule: {
        cron: cronString,
        duration: `${duration}`,
      }
    },
    cronData: fromCron(cronString, duration)
  }
}

export function useAvailabilityData(
  availabilityData: AvailabilityTimeslotData[]
) {
  return useMemo(() => {
    const timeslotData: Record<string, AvailabilityTimeslotData> = {}

    const itemData: Record<string, CalendarItemData[]> = {
      daily: [],
      weekly: [],
      monthly: [],
      unknown: [],
    }

    availabilityData.forEach((data, index) => {
      timeslotData[data.id] = data;
      
      const { date, cronType } = data.cronData;
      const duration = parseInt(data.timeslot.schedule.duration);
      itemData[cronType || "unknown"]?.push({ start: date, duration, itemId: data.id })
    })

    return { timeslotData, itemData}
  }, [availabilityData])
}

export function earliestAcrossAll(
  availabilities: Availability[],
  def = timeOnDate(new Date(), new Date(0, 0, 0, 8, 0, 0, 0)),
): Date {
  if(availabilities.length === 0) { return def }

  return availabilities.reduce((prev, curr) => {
    return timeOnDate(curr.start, prev.start) < curr.start ? prev : curr;
  }, availabilities[0]).start; 
}

export function latestAcrossAll(
  availabilities: Availability[],
  def = timeOnDate(new Date(), new Date(0, 0, 0, 18, 0, 0, 0)),
): Date {
  if(availabilities.length === 0) { return def }

  return availabilities.reduce((prev, curr) => {
    return timeOnDate(curr.end, prev.end) > curr.end ? prev : curr;
  }, availabilities[0]).end; 
}
